"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from "framer-motion";
import { useAuth } from "./Auth";
import ModernNavigation from "./ModernNavigation";
import FeaturesShowcase from "./FeaturesShowcase";
import { 
  Sparkles, 
  Brain, 
  Palette, 
  Video, 
  BarChart3, 
  Code, 
  Languages, 
  FileText,
  TrendingUp,
  Mail,
  Target,
  Globe,
  Zap,
  ArrowRight,
  Star,
  Users,
  Cpu,
  Lightbulb,
  Shield,
  Clock,
  Rocket,
  Award,
  CheckCircle
} from "lucide-react";

export default function UltraModernHomePage() {
  const { user } = useAuth();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    const handleScroll = () => setScrollY(window.scrollY);
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const features = [
    {
      icon: Palette,
      title: "Geração de Imagens",
      description: "Crie imagens impressionantes com IA a partir de descrições textuais.",
      color: "from-blue-500 to-cyan-500",
      gradient: "bg-gradient-to-br from-blue-50 to-cyan-50",
      borderColor: "border-blue-200"
    },
    {
      icon: FileText,
      title: "Gerador de Pitch",
      description: "Desenvolva pitches de vendas persuasivos e profissionais.",
      color: "from-green-500 to-emerald-500",
      gradient: "bg-gradient-to-br from-green-50 to-emerald-50",
      borderColor: "border-green-200"
    },
    {
      icon: Video,
      title: "Script de Vídeo",
      description: "Gere scripts completos para vídeos com estrutura profissional.",
      color: "from-purple-500 to-pink-500",
      gradient: "bg-gradient-to-br from-purple-50 to-pink-50",
      borderColor: "border-purple-200"
    },
    {
      icon: Brain,
      title: "Análise de Sentimento",
      description: "Analise textos para detectar emoções e insights.",
      color: "from-red-500 to-orange-500",
      gradient: "bg-gradient-to-br from-red-50 to-orange-50",
      borderColor: "border-red-200"
    }
  ];

  const stats = [
    { icon: Cpu, value: "15+", label: "Ferramentas IA", delay: 0 },
    { icon: Users, value: "10K+", label: "Usuários Ativos", delay: 0.1 },
    { icon: Shield, value: "100%", label: "Seguro", delay: 0.2 },
    { icon: Clock, value: "24/7", label: "Disponível", delay: 0.3 }
  ];

  if (user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <ModernNavigation />
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center justify-center min-h-screen -mt-16"
        >
          <div className="text-center p-8">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"
            />
            <p className="text-gray-600 text-lg">Redirecionando para seu painel...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Animated Background */}
      <motion.div 
        className="fixed inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 opacity-50"
        style={{ y: backgroundY }}
      />
      
      {/* Mouse Follower */}
      <motion.div
        className="fixed w-96 h-96 rounded-full bg-purple-500/10 blur-3xl pointer-events-none z-0"
        animate={{
          x: mousePosition.x - 192,
          y: mousePosition.y - 192,
        }}
        transition={{ type: "spring", stiffness: 100, damping: 30 }}
      />

      <ModernNavigation />
      
      {/* Hero Section - Ultra Modern */}
      <motion.section 
        className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white"
        style={{ opacity }}
      >
        {/* Animated Background Elements */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full"
            animate={{
              x: [0, Math.random() * 100 - 50],
              y: [0, Math.random() * 100 - 50],
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "easeInOut"
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
        
        <div className="relative px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-32">
          <div className="text-center max-w-5xl mx-auto">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="mb-8 flex justify-center"
            >
              <motion.div 
                className="bg-white/10 backdrop-blur-sm rounded-full p-4 inline-flex"
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                <Sparkles className="w-12 h-12 text-yellow-300" />
              </motion.div>
            </motion.div>
            
            <motion.h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <motion.span 
                className="block text-yellow-300"
                whileHover={{ scale: 1.05 }}
              >
                Transforme Ideias em
              </motion.span>
              <motion.span 
                className="block"
                whileHover={{ scale: 1.05 }}
              >
                Realidade com IA
              </motion.span>
            </motion.h1>
            
            <motion.p 
              className="text-xl sm:text-2xl lg:text-3xl mb-10 text-white/90 max-w-4xl mx-auto leading-relaxed"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              A plataforma mais completa de inteligência artificial para criadores, 
              desenvolvedores e profissionais de marketing
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <motion.a
                href="/login"
                className="group w-full sm:w-auto px-10 py-5 bg-white text-purple-600 font-bold rounded-2xl hover:bg-gray-100 transition-all duration-300 text-xl shadow-2xl hover:shadow-3xl flex items-center justify-center gap-3"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <Rocket className="w-6 h-6" />
                Começar Agora
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </motion.a>
              <motion.a
                href="/admin"
                className="group w-full sm:w-auto px-10 py-5 bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold rounded-2xl hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 text-xl shadow-2xl hover:shadow-3xl flex items-center justify-center gap-3"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <Shield className="w-6 h-6" />
                Painel Admin
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </motion.a>
              <motion.a
                href="#features"
                className="group w-full sm:w-auto px-10 py-5 bg-transparent border-3 border-white text-white font-bold rounded-2xl hover:bg-white hover:text-purple-600 transition-all duration-300 text-xl flex items-center justify-center gap-3"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <Lightbulb className="w-6 h-6" />
                Ver Funcionalidades
              </motion.a>
            </motion.div>
            
            {/* Animated Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.8 + stat.delay, duration: 0.5 }}
                  whileHover={{ scale: 1.1, y: -5 }}
                >
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20">
                    <motion.div
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                      <stat.icon className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-3 text-yellow-300" />
                    </motion.div>
                    <motion.div 
                      className="text-3xl sm:text-4xl font-bold mb-2"
                      whileHover={{ scale: 1.2 }}
                    >
                      {stat.value}
                    </motion.div>
                    <div className="text-sm text-white/80">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Features Section - Enhanced */}
      <motion.section 
        id="features" 
        className="py-20 sm:py-24 bg-gray-50 relative"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16 sm:mb-20"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.h2 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              whileHover={{ scale: 1.05 }}
            >
              Funcionalidades
              <motion.span 
                className="block text-purple-600"
                whileHover={{ scale: 1.1 }}
              >
                Revolucionárias
              </motion.span>
            </motion.h2>
            <motion.p 
              className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto"
              whileHover={{ scale: 1.02 }}
            >
              Descubra o poder da inteligência artificial com nossas ferramentas especializadas
            </motion.p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className={`group relative bg-white rounded-3xl shadow-xl hover:shadow-3xl transition-all duration-500 overflow-hidden border ${feature.borderColor}`}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -10,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
                }}
              >
                <motion.div 
                  className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                  whileHover={{ scale: 1.1 }}
                />
                
                <motion.div 
                  className={`bg-gradient-to-br ${feature.gradient} p-8 sm:p-10 relative`}
                  whileHover={{ scale: 1.02 }}
                >
                  <motion.div 
                    className={`bg-gradient-to-r ${feature.color} w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mb-6 shadow-2xl`}
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <feature.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </motion.div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                </motion.div>
                
                <motion.div 
                  className="p-6 sm:p-8"
                  whileHover={{ scale: 1.02 }}
                >
                  <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <motion.button
                    className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 group"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Experimentar</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                  </motion.button>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Complete Features Showcase */}
      <FeaturesShowcase />

      {/* Enhanced CTA Section */}
      <motion.section 
        className="py-20 sm:py-24 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        {/* Animated Background Elements */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-32 h-32 bg-white/10 rounded-full"
            animate={{
              x: [Math.random() * 200 - 100, Math.random() * 200 - 100],
              y: [Math.random() * 200 - 100, Math.random() * 200 - 100],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: Math.random() * 5 + 5,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
        
        <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            whileInView={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="mb-8"
          >
            <Award className="w-20 h-20 sm:w-24 sm:h-24 mx-auto text-yellow-300" />
          </motion.div>
          
          <motion.h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            viewport={{ once: true }}
          >
            Pronto para
            <motion.span 
              className="block text-yellow-300"
              whileHover={{ scale: 1.1 }}
            >
              Revolucionar seu Trabalho?
            </motion.span>
          </motion.h2>
          
          <motion.p 
            className="text-xl sm:text-2xl mb-12 text-white/90 max-w-3xl mx-auto leading-relaxed"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            viewport={{ once: true }}
          >
            Junte-se a milhares de profissionais que já estão usando IA para 
            transformar suas ideias em realidade
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-6 justify-center"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.a
              href="/login"
              className="group w-full sm:w-auto px-12 py-5 bg-white text-purple-600 font-bold rounded-2xl hover:bg-gray-100 transition-all duration-300 text-xl shadow-2xl hover:shadow-3xl flex items-center justify-center gap-3"
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <CheckCircle className="w-6 h-6" />
              Começar Gratuitamente
              <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
            </motion.a>
          </motion.div>
        </div>
      </motion.section>

      {/* Enhanced Footer */}
      <footer className="bg-gray-900 text-white py-16 sm:py-20">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-2">
              <motion.div 
                className="flex items-center mb-6"
                whileHover={{ scale: 1.05 }}
              >
                <Sparkles className="w-10 h-10 text-purple-400 mr-3" />
                <h3 className="text-2xl sm:text-3xl font-bold">Axion IA Panel</h3>
              </motion.div>
              <p className="text-gray-400 mb-8 text-base sm:text-lg leading-relaxed">
                A plataforma mais completa de inteligência artificial para profissionais. 
                Transforme ideias em realidade com nossas ferramentas avançadas.
              </p>
              <div className="flex gap-4">
                <motion.div 
                  className="bg-purple-600/20 rounded-xl p-4"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <Shield className="w-6 h-6 text-purple-400" />
                </motion.div>
                <motion.div 
                  className="bg-green-600/20 rounded-xl p-4"
                  whileHover={{ scale: 1.1, rotate: -5 }}
                >
                  <Clock className="w-6 h-6 text-green-400" />
                </motion.div>
              </div>
            </div>
            
            <div>
              <h4 className="text-xl font-semibold mb-6">Funcionalidades</h4>
              <ul className="space-y-3 text-gray-400 text-base sm:text-lg">
                {[
                  { icon: Palette, text: "Geração de Imagens" },
                  { icon: FileText, text: "Criação de Pitches" },
                  { icon: Video, text: "Scripts de Vídeo" },
                  { icon: Brain, text: "Análise de Sentimento" }
                ].map((item, index) => (
                  <motion.li 
                    key={index}
                    className="flex items-center gap-3"
                    whileHover={{ x: 10 }}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.text}
                  </motion.li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-xl font-semibold mb-6">Empresa</h4>
              <ul className="space-y-3 text-gray-400 text-base sm:text-lg">
                {["Sobre Nós", "Contato", "Blog", "Carreiras"].map((item, index) => (
                  <motion.li key={index}>
                    <motion.a 
                      href="#" 
                      className="hover:text-white transition-colors block"
                      whileHover={{ x: 10 }}
                    >
                      {item}
                    </motion.a>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-400 text-base sm:text-lg">
              &copy; 2026 Axion IA Panel. Todos os direitos reservados.
            </p>
            <motion.p 
              className="text-gray-500 text-sm sm:text-base mt-3"
              whileHover={{ scale: 1.05 }}
            >
              Feito com ❤️ usando inteligência artificial avançada
            </motion.p>
          </div>
        </div>
      </footer>
    </div>
  );
}
